import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageWithNavbar from '../components/PageWithNavbar';
import ProductSelectionModal from '../components/ProductSelectionModal';
import { API_BASE_URL } from '../lib/constants';

interface Recipe {
  id: number;
  title: string;
  description: string;
  servings: number;
  prep_time_minutes?: number;
  cook_time_minutes?: number;
  total_time_minutes?: number;
  estimated_cost_per_serving?: string;
  estimated_calories_per_serving?: number;
  is_favorite: boolean;
  source_url?: string;
  source_name?: string;
  tags?: string[];
  notes?: string;
  created_at: string;
  updated_at: string;
  has_modifications: boolean;
  current_ingredients?: string[];
  current_instructions?: string[];
  current_sections?: any[];
  has_sections?: boolean;
  original_ingredients?: string[];
  original_instructions?: string[];
  original_sections?: any[];
  ingredients?: string[];
  instructions?: string[];
  sections?: any[];
  cost_calculations?: any[];
  parser_version?: string;
  last_parsed_at?: string;
}

export default function RecipeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editedRecipe, setEditedRecipe] = useState<Partial<Recipe>>({});
  const [calculatingCost, setCalculatingCost] = useState(false);
  const [calculatingCalories, setCalculatingCalories] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  const [showCostBreakdown, setShowCostBreakdown] = useState(false);
  const [costBreakdown, setCostBreakdown] = useState<any>(null);
  const [recalculatingIngredients, setRecalculatingIngredients] = useState<{[key: number]: boolean}>({});
  const [showProductSelectionModal, setShowProductSelectionModal] = useState(false);
  const [selectedIngredientIndex, setSelectedIngredientIndex] = useState<number | null>(null);
  const [selectedIngredientText, setSelectedIngredientText] = useState('');
  const [selectedIngredientSearchTerm, setSelectedIngredientSearchTerm] = useState('');
  const [selectedIngredientData, setSelectedIngredientData] = useState<any>(null);
  const [reparsing, setReparsing] = useState(false);
  const token = localStorage.getItem('token');

  const fetchRecipe = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/recipes/${id}/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setRecipe(data);
        setEditedRecipe(data);
        
        // Check if there are existing cost calculations
        if (data.cost_calculations && data.cost_calculations.length > 0) {
          const latestCalculation = data.cost_calculations[0];
          setCostBreakdown({
            cost_per_serving: parseFloat(data.estimated_cost_per_serving || latestCalculation.cost_per_serving),
            total_cost: parseFloat(latestCalculation.total_cost),
            ingredient_breakdown: latestCalculation.ingredient_details || []
          });
        }
      } else if (response.status === 404) {
        navigate('/recipes');
      }
    } catch (error) {
      console.error('Error fetching recipe:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveRecipe = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/recipes/${id}/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editedRecipe),
      });
      
      if (response.ok) {
        const updatedRecipe = await response.json();
        setRecipe(updatedRecipe);
        setEditing(false);
      }
    } catch (error) {
      console.error('Error saving recipe:', error);
    }
  };

  const toggleFavorite = async () => {
    if (!recipe) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/recipes/${recipe.id}/toggle-favorite/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setRecipe({ ...recipe, is_favorite: data.is_favorite });
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const calculateCost = async () => {
    if (!recipe) return;
    
    setCalculatingCost(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/recipes/${recipe.id}/calculate-cost/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setRecipe({ 
            ...recipe, 
            estimated_cost_per_serving: data.cost_per_serving.toString()
          });
          setCostBreakdown({
            cost_per_serving: parseFloat(data.cost_per_serving),
            total_cost: parseFloat(data.total_cost),
            ingredient_breakdown: data.ingredient_breakdown || []
          });
        } else {
          alert('Unable to calculate cost at this time');
        }
      }
    } catch (error) {
      console.error('Error calculating cost:', error);
    } finally {
      setCalculatingCost(false);
    }
  };

  const calculateCalories = async () => {
    if (!recipe) return;
    
    setCalculatingCalories(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/recipes/${recipe.id}/calculate-calories/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setRecipe({ 
            ...recipe, 
            estimated_calories_per_serving: data.calories_per_serving
          });
        } else {
          alert('Unable to calculate calories at this time');
        }
      }
    } catch (error) {
      console.error('Error calculating calories:', error);
    } finally {
      setCalculatingCalories(false);
    }
  };

  const reparseRecipe = async () => {
    if (!recipe || !recipe.source_url) {
      alert('This recipe cannot be re-parsed as it does not have a source URL');
      return;
    }

    setReparsing(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/recipes/${recipe.id}/re-parse/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          current_version: recipe.parser_version
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          alert(`Recipe successfully re-parsed with parser v${data.new_version}`);
          // Reload the recipe to get the updated data
          fetchRecipe();
        } else {
          alert(data.message || 'Unable to re-parse recipe at this time');
        }
      } else {
        const errorData = await response.json();
        alert(errorData.message || 'Error re-parsing recipe');
      }
    } catch (error) {
      console.error('Error re-parsing recipe:', error);
      alert('Error re-parsing recipe');
    } finally {
      setReparsing(false);
    }
  };

  const printRecipe = () => {
    if (!recipe) return;
    window.open(`/recipe/${recipe.id}/print`, '_blank');
  };

  const deleteRecipe = async () => {
    if (!recipe || !confirm('Are you sure you want to delete this recipe?')) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/recipes/${recipe.id}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        navigate('/recipes');
      }
    } catch (error) {
      console.error('Error deleting recipe:', error);
    }
  };

  const generateDefaultSearchTerm = (ingredientText: string): string => {
    // Simple client-side extraction similar to backend logic
    let text = ingredientText.toLowerCase().trim();
    
    // Remove quantities and fractions from the beginning
    text = text.replace(/^\d+[./]?\d*(\s*(-|to)\s*\d+[./]?\d*)?\s*/, '');
    text = text.replace(/\(\d+[./]?\d*\s*[^)]*\)/g, '');
    
    // Remove common measurement words
    const measurementWords = ['cup', 'cups', 'tablespoon', 'tablespoons', 'tbsp', 'teaspoon', 'teaspoons', 'tsp', 'pound', 'pounds', 'lb', 'lbs', 'ounce', 'ounces', 'oz', 'gram', 'grams', 'g', 'kilogram', 'kilograms', 'kg', 'liter', 'liters', 'l', 'milliliter', 'milliliters', 'ml', 'pint', 'pints', 'quart', 'quarts', 'gallon', 'gallons', 'piece', 'pieces', 'slice', 'slices', 'clove', 'cloves', 'stick', 'sticks', 'can', 'cans', 'jar', 'jars', 'bottle', 'bottles', 'package', 'packages', 'box', 'boxes', 'bag', 'bags'];
    
    const words = text.split(/\s+/);
    const filteredWords = words.filter(word => !measurementWords.includes(word));
    
    // Remove common descriptive words
    const descriptiveWords = ['diced', 'minced', 'sliced', 'shredded', 'grated', 'raw', 'cooked', 'fresh', 'frozen', 'dried', 'canned'];
    const finalWords = filteredWords.filter(word => !descriptiveWords.includes(word));
    
    // Take up to 2 meaningful words
    return finalWords.slice(0, 2).join(' ').trim() || ingredientText.trim();
  };

  const openProductSelectionModal = (ingredientIndex: number, ingredientData: any) => {
    const ingredientText = ingredientData.ingredient || '';
    // Use saved search term if available, otherwise generate default
    const searchTerm = ingredientData.search_term_used || generateDefaultSearchTerm(ingredientText);
    
    setSelectedIngredientIndex(ingredientIndex);
    setSelectedIngredientText(ingredientText);
    setSelectedIngredientSearchTerm(searchTerm);
    setSelectedIngredientData(ingredientData);
    setShowProductSelectionModal(true);
  };

  const handleProductSelected = async (updatedIngredient: any) => {
    // Refresh the recipe data to get updated cost information
    await fetchRecipe();
    setShowProductSelectionModal(false);
    setSelectedIngredientIndex(null);
    setSelectedIngredientText('');
    setSelectedIngredientSearchTerm('');
    setSelectedIngredientData(null);
  };


  useEffect(() => {
    if (id) {
      fetchRecipe();
    }
  }, [id]);

  const formatTime = (minutes?: number) => {
    if (!minutes) return '';
    if (minutes < 60) return `${minutes} minutes`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours} hour${hours !== 1 ? 's' : ''}`;
  };

  if (loading) {
    return (
      <PageWithNavbar>
        <section style={{ padding: '14px 32px 0 32px' }}>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <p>Loading recipe...</p>
          </div>
        </section>
      </PageWithNavbar>
    );
  }

  if (!recipe) {
    return (
      <PageWithNavbar>
        <section style={{ padding: '14px 32px 0 32px' }}>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <p>Recipe not found</p>
          </div>
        </section>
      </PageWithNavbar>
    );
  }

  const ingredients = showOriginal && recipe.original_ingredients 
    ? recipe.original_ingredients 
    : recipe.current_ingredients || [];
    
  const instructions = showOriginal && recipe.original_instructions 
    ? recipe.original_instructions 
    : recipe.current_instructions || [];

  const sections = showOriginal && recipe.original_sections 
    ? recipe.original_sections 
    : recipe.current_sections || [];

  // Helper function to render ingredients with section headers
  const renderIngredientItem = (ingredient: string, index: number, allIngredients: string[]) => {
    if (ingredient.startsWith('# ')) {
      // This is a section header
      return (
        <li key={index} style={{ 
          padding: '16px 0 8px 0', 
          fontSize: 16,
          fontWeight: 600,
          color: '#374151',
          borderBottom: '2px solid #e5e7eb',
          marginBottom: 8,
          listStyle: 'none',
          marginLeft: -20
        }}>
          {ingredient.substring(2)}
        </li>
      );
    } else {
      // Regular ingredient
      return (
        <li key={index} style={{ 
          padding: '8px 0', 
          borderBottom: index < allIngredients.length - 1 && !allIngredients[index + 1]?.startsWith('# ') ? '1px solid #f3f4f6' : 'none',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8
        }}>
          <span style={{ color: '#10b981', fontSize: 18, lineHeight: 1 }}>•</span>
          <span>{ingredient}</span>
        </li>
      );
    }
  };

  // Helper function to render instructions with section headers
  const renderInstructionItem = (instruction: string, index: number, allInstructions: string[]) => {
    if (instruction.startsWith('## ')) {
      // This is a section header
      return (
        <li key={index} style={{ 
          padding: '16px 0 8px 0', 
          fontSize: 16,
          fontWeight: 600,
          color: '#374151',
          borderBottom: '2px solid #e5e7eb',
          marginBottom: 8,
          listStyle: 'none',
          marginLeft: -20
        }}>
          {instruction.substring(3)}
        </li>
      );
    } else {
      // Regular instruction
      return (
        <li key={index} style={{ 
          padding: '8px 0', 
          lineHeight: 1.5,
          marginBottom: 8
        }}>
          {instruction}
        </li>
      );
    }
  };

  return (
    <PageWithNavbar>
      <section style={{ padding: '14px 32px 0 32px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
          <div style={{ flex: 1 }}>
            <button
              onClick={() => navigate('/recipes')}
              style={{
                background: 'none',
                border: 'none',
                color: '#3b82f6',
                fontSize: 14,
                cursor: 'pointer',
                marginBottom: 12
              }}
            >
              ← Back to Recipes
            </button>
            
            {editing ? (
              <input
                type="text"
                value={editedRecipe.title || ''}
                onChange={(e) => setEditedRecipe({ ...editedRecipe, title: e.target.value })}
                style={{
                  fontSize: 28,
                  fontWeight: 700,
                  margin: 0,
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  padding: '8px 12px',
                  width: '100%',
                  maxWidth: 600
                }}
              />
            ) : (
              <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0 }}>{recipe.title}</h1>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: 8, marginLeft: 16 }}>
            <button
              id="favorite-heart"
              className="favorite-button"
              onClick={toggleFavorite}
              style={{
                background: 'none',
                border: 'none',
                fontSize: 24,
                cursor: 'pointer',
                color: recipe.is_favorite ? '#ef4444' : '#9ca3af',
                padding: 8
              }}
              title={recipe.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
            >
              {recipe.is_favorite ? '❤️' : '🤍'}
            </button>
            
            {editing ? (
              <>
                <button
                  onClick={saveRecipe}
                  style={{
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: 6,
                    fontSize: 14,
                    cursor: 'pointer'
                  }}
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setEditing(false);
                    setEditedRecipe(recipe);
                  }}
                  style={{
                    background: '#f3f4f6',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    padding: '8px 16px',
                    borderRadius: 6,
                    fontSize: 14,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setEditing(true)}
                  style={{
                    background: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: 6,
                    fontSize: 14,
                    cursor: 'pointer'
                  }}
                >
                  Edit
                </button>
                <button
                  onClick={printRecipe}
                  style={{
                    background: '#6b7280',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: 6,
                    fontSize: 14,
                    cursor: 'pointer'
                  }}
                >
                  🖨️ Print
                </button>
                {recipe.source_url && (
                  <button
                    onClick={reparseRecipe}
                    disabled={reparsing}
                    style={{
                      background: reparsing ? '#9ca3af' : '#8b5cf6',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: 6,
                      fontSize: 14,
                      cursor: reparsing ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {reparsing ? '🔄 Re-parsing...' : '🔄 Re-parse'}
                  </button>
                )}
                <button
                  onClick={deleteRecipe}
                  style={{
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: 6,
                    fontSize: 14,
                    cursor: 'pointer'
                  }}
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 }}>
          {/* Main Content */}
          <div>
            {/* Description */}
            {(editing || recipe.description) && (
              <div style={{
                background: '#fff',
                borderRadius: 12,
                padding: 20,
                marginBottom: 20,
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
              }}>
                <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Description</h2>
                {editing ? (
                  <textarea
                    value={editedRecipe.description || ''}
                    onChange={(e) => setEditedRecipe({ ...editedRecipe, description: e.target.value })}
                    placeholder="Add a description..."
                    style={{
                      width: '100%',
                      minHeight: 80,
                      border: '1px solid #d1d5db',
                      borderRadius: 6,
                      padding: '8px 12px',
                      fontSize: 14,
                      resize: 'vertical'
                    }}
                  />
                ) : (
                  <p style={{ margin: 0, lineHeight: 1.5 }}>{recipe.description}</p>
                )}
              </div>
            )}

            {/* Original/Modified Toggle */}
            {recipe.has_modifications && recipe.original_ingredients && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                  <input
                    type="checkbox"
                    checked={showOriginal}
                    onChange={(e) => setShowOriginal(e.target.checked)}
                  />
                  Show original recipe (before modifications)
                </label>
              </div>
            )}

            {/* Ingredients */}
            <div style={{
              background: '#fff',
              borderRadius: 12,
              padding: 20,
              marginBottom: 20,
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
            }}>
              <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Ingredients</h2>
              {editing ? (
                <div>
                  <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                    Enter each ingredient on a new line
                  </p>
                  <textarea
                    value={Array.isArray(editedRecipe.ingredients) 
                      ? editedRecipe.ingredients.join('\n') 
                      : ingredients.join('\n')}
                    onChange={(e) => setEditedRecipe({ 
                      ...editedRecipe, 
                      ingredients: e.target.value.split('\n').filter(line => line.trim())
                    })}
                    style={{
                      width: '100%',
                      minHeight: 200,
                      border: '1px solid #d1d5db',
                      borderRadius: 6,
                      padding: '8px 12px',
                      fontSize: 14,
                      fontFamily: 'monospace',
                      resize: 'vertical'
                    }}
                  />
                </div>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {ingredients.map((ingredient, index) => renderIngredientItem(ingredient, index, ingredients))}
                </ul>
              )}
            </div>

            {/* Instructions */}
            <div style={{
              background: '#fff',
              borderRadius: 12,
              padding: 20,
              marginBottom: 20,
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
            }}>
              <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Instructions</h2>
              {editing ? (
                <div>
                  <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                    Enter each step on a new line
                  </p>
                  <textarea
                    value={Array.isArray(editedRecipe.instructions) 
                      ? editedRecipe.instructions.join('\n') 
                      : instructions.join('\n')}
                    onChange={(e) => setEditedRecipe({ 
                      ...editedRecipe, 
                      instructions: e.target.value.split('\n').filter(line => line.trim())
                    })}
                    style={{
                      width: '100%',
                      minHeight: 300,
                      border: '1px solid #d1d5db',
                      borderRadius: 6,
                      padding: '8px 12px',
                      fontSize: 14,
                      fontFamily: 'monospace',
                      resize: 'vertical'
                    }}
                  />
                </div>
              ) : (
                <ol style={{ paddingLeft: 20 }}>
                  {instructions.map((instruction, index) => renderInstructionItem(instruction, index, instructions))}
                </ol>
              )}
            </div>

            {/* Notes */}
            <div style={{
              background: '#fff',
              borderRadius: 12,
              padding: 20,
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
            }}>
              <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Personal Notes</h2>
              {editing ? (
                <textarea
                  value={editedRecipe.notes || ''}
                  onChange={(e) => setEditedRecipe({ ...editedRecipe, notes: e.target.value })}
                  placeholder="Add your personal notes, modifications, or tips..."
                  style={{
                    width: '100%',
                    minHeight: 100,
                    border: '1px solid #d1d5db',
                    borderRadius: 6,
                    padding: '8px 12px',
                    fontSize: 14,
                    resize: 'vertical'
                  }}
                />
              ) : (
                <div>
                  {recipe.notes ? (
                    <p style={{ margin: 0, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{recipe.notes}</p>
                  ) : (
                    <p style={{ 
                      margin: 0, 
                      color: '#9ca3af', 
                      fontStyle: 'italic',
                      cursor: 'pointer'
                    }} onClick={() => setEditing(true)}>
                      Click to add personal notes...
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div>
            {/* Recipe Info */}
            <div style={{
              background: '#fff',
              borderRadius: 12,
              padding: 20,
              marginBottom: 20,
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
            }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Recipe Info</h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6b7280' }}>Servings:</span>
                  <span style={{ fontWeight: 500 }}>{recipe.servings}</span>
                </div>
                
                {recipe.prep_time_minutes && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280' }}>Prep time:</span>
                    <span style={{ fontWeight: 500 }}>{formatTime(recipe.prep_time_minutes)}</span>
                  </div>
                )}
                
                {recipe.cook_time_minutes && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280' }}>Cook time:</span>
                    <span style={{ fontWeight: 500 }}>{formatTime(recipe.cook_time_minutes)}</span>
                  </div>
                )}
                
                {recipe.total_time_minutes && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid #f3f4f6' }}>
                    <span style={{ color: '#6b7280' }}>Total time:</span>
                    <span style={{ fontWeight: 600 }}>{formatTime(recipe.total_time_minutes)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Cost & Nutrition */}
            <div style={{
              background: '#fff',
              borderRadius: 12,
              padding: 20,
              marginBottom: 20,
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
            }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Cost & Nutrition</h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* Primary display: Total cost per dish */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <span style={{ color: '#6b7280' }}>Total cost:</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {costBreakdown ? (
                      <button
                        onClick={() => setShowCostBreakdown(true)}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontWeight: 700,
                          color: '#059669',
                          fontSize: 18,
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          padding: 0
                        }}
                      >
                        ${Number(costBreakdown.total_cost).toFixed(2)}
                      </button>
                    ) : recipe.estimated_cost_per_serving ? (
                      <span style={{ 
                        fontWeight: 700, 
                        color: '#059669', 
                        fontSize: 18
                      }}>
                        ${(parseFloat(recipe.estimated_cost_per_serving) * recipe.servings).toFixed(2)}
                      </span>
                    ) : (
                      <span style={{ color: '#9ca3af', fontSize: 12 }}>Not calculated</span>
                    )}
                    <button
                      id="refresh-cost"
                      onClick={calculateCost}
                      disabled={calculatingCost}
                      style={{
                        background: '#f3f4f6',
                        border: '1px solid #d1d5db',
                        borderRadius: 4,
                        padding: '4px 8px',
                        fontSize: 11,
                        cursor: calculatingCost ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {calculatingCost ? '...' : '🔄'}
                    </button>
                  </div>
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#6b7280' }}>Cost per serving:</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {costBreakdown ? (
                      <button
                        onClick={() => setShowCostBreakdown(true)}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontWeight: 600,
                          color: '#059669',
                          fontSize: 16,
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          padding: 0
                        }}
                      >
                        ${Number(costBreakdown.cost_per_serving).toFixed(2)}
                      </button>
                    ) : recipe.estimated_cost_per_serving ? (
                      <span style={{ fontWeight: 600, color: '#059669' }}>
                        ${recipe.estimated_cost_per_serving}
                      </span>
                    ) : (
                      <span style={{ color: '#9ca3af', fontSize: 12 }}>Not calculated</span>
                    )}
                  </div>
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#6b7280' }}>Calories per serving:</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {recipe.estimated_calories_per_serving ? (
                      <span style={{ fontWeight: 600, color: '#dc2626' }}>
                        {recipe.estimated_calories_per_serving} cal
                      </span>
                    ) : (
                      <span style={{ color: '#9ca3af', fontSize: 12 }}>Not calculated</span>
                    )}
                    <button
                      id="refresh-calories"
                      onClick={calculateCalories}
                      disabled={calculatingCalories}
                      style={{
                        background: '#f3f4f6',
                        border: '1px solid #d1d5db',
                        borderRadius: 4,
                        padding: '4px 8px',
                        fontSize: 11,
                        cursor: calculatingCalories ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {calculatingCalories ? '...' : '🔄'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Source */}
            {recipe.source_url && (
              <div style={{
                background: '#fff',
                borderRadius: 12,
                padding: 20,
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
              }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Source</h3>
                <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 8 }}>
                  {recipe.source_name || 'Web Recipe'}
                </div>
                <a
                  href={recipe.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    color: '#3b82f6',
                    textDecoration: 'none',
                    fontSize: 14
                  }}
                >
                  View Original Recipe 🔗
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Cost Breakdown Modal */}
        {showCostBreakdown && costBreakdown && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: 12,
              padding: 24,
              maxWidth: 600,
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h2 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>{recipe.title} - Cost Breakdown</h2>
                <button
                  onClick={() => setShowCostBreakdown(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: 18,
                    cursor: 'pointer',
                    color: '#6b7280'
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Summary */}
              <div style={{
                background: '#f9fafb',
                borderRadius: 8,
                padding: 16,
                marginBottom: 20
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontWeight: 500 }}>Total Cost:</span>
                  <span style={{ fontWeight: 600, color: '#059669' }}>
                    ${Number(costBreakdown.total_cost).toFixed(2)}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontWeight: 500 }}>Servings:</span>
                  <span>{recipe.servings}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid #e5e7eb' }}>
                  <span style={{ fontWeight: 600 }}>Cost per Serving:</span>
                  <span style={{ fontWeight: 600, color: '#059669' }}>
                    ${Number(costBreakdown.cost_per_serving).toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Ingredient Breakdown */}
              <div>
                <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Ingredient Cost Breakdown</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {costBreakdown.ingredient_breakdown && costBreakdown.ingredient_breakdown.length > 0 ? (
                    costBreakdown.ingredient_breakdown.map((item: any, index: number) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          padding: '12px 0',
                          borderBottom: index < costBreakdown.ingredient_breakdown.length - 1 ? '1px solid #f3f4f6' : 'none'
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 500, marginBottom: 4 }}>
                            {item.ingredient}
                          </div>
                          {item.product_name ? (
                            <div style={{ fontSize: 12, color: '#6b7280' }}>
                              Based on: {item.product_url ? (
                                <a
                                  href={item.product_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: '#3b82f6',
                                    textDecoration: 'underline',
                                    fontWeight: 500
                                  }}
                                >
                                  {item.product_name}
                                </a>
                              ) : (
                                <span style={{ fontWeight: 500 }}>{item.product_name}</span>
                              )} (${Number(item.product_price || 0).toFixed(2)})
                            </div>
                          ) : (
                            <div style={{ fontSize: 12, color: '#ef4444' }}>
                              No product found for pricing
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 160 }}>
                          <div style={{ textAlign: 'right', flex: 1 }}>
                            {item.estimated_cost ? (
                              <div>
                                <span style={{ fontWeight: 600, color: '#059669' }}>
                                  ${Number(item.estimated_cost).toFixed(2)}
                                </span>
                                {item.is_manual_override && (
                                  <div style={{ fontSize: 10, color: '#f59e0b', fontWeight: 500 }}>
                                    Manual
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span style={{ color: '#9ca3af', fontSize: 12 }}>
                                N/A
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <button
                              onClick={() => openProductSelectionModal(index, item)}
                              style={{
                                background: '#3b82f6',
                                color: 'white',
                                border: 'none',
                                borderRadius: 4,
                                padding: '6px 8px',
                                fontSize: 10,
                                cursor: 'pointer',
                                fontWeight: 500
                              }}
                              title="Select different product"
                            >
                              🛒 Select
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p style={{ color: '#6b7280', fontStyle: 'italic' }}>
                      No detailed breakdown available
                    </p>
                  )}
                </div>
              </div>

              <div style={{
                marginTop: 20,
                padding: 12,
                background: '#fef3c7',
                borderRadius: 6,
                fontSize: 12,
                color: '#92400e'
              }}>
                <strong>Note:</strong> Cost estimates are based on Walmart pricing and assume you use approximately 25% of each product. 
                Actual costs may vary based on local pricing, product sizes, and usage amounts.
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
                <button
                  onClick={() => setShowCostBreakdown(false)}
                  style={{
                    background: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: 6,
                    fontSize: 14,
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Product Selection Modal */}
        <ProductSelectionModal
          isOpen={showProductSelectionModal}
          onClose={() => setShowProductSelectionModal(false)}
          recipeId={recipe.id}
          ingredientIndex={selectedIngredientIndex || 0}
          ingredientText={selectedIngredientText}
          defaultSearchTerm={selectedIngredientSearchTerm}
          onProductSelected={handleProductSelected}
          existingProductData={selectedIngredientData}
        />
      </section>
    </PageWithNavbar>
  );
}